import {
  LayoutDashboard,
  BarChart3,
  FlaskConical,
  Layers,
  ScanSearch,
  Ban,
} from "lucide-react";
import { SectionTitle, Sub, RefLink, P, ConnectsTo } from "./GuideHelpers";
import { PageCard } from "./PageCard";

export const SectionRankTrackerAnalytics = () => (
  <>
    <SectionTitle id="rank-tracker" scope="brand">Rank Tracker</SectionTitle>
    <PageCard icon={BarChart3} title="Rank Tracker" route="/rank-tracker" purpose="Monitor keyword rankings over time to measure if your SEO decisions are working.">
      <P>
        The Rank Tracker is your <strong>ongoing monitoring tool</strong>. While Landscapes give you
        point-in-time snapshots, the Rank Tracker provides continuous rank data so you can see trends,
        detect drops, and correlate rank changes with sales.
      </P>
      <P>
        Keywords arrive in Rank Tracker in two ways: automatically when added to the <strong>Master
        List</strong> in the Keyword Bank, or manually via the "Add to Rank Tracker" action on any
        keyword. Note that manually adding a keyword to Rank Tracker does <strong>not</strong> add
        it to the Master List.
      </P>
      <Sub>Key features</Sub>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li><strong className="text-foreground">Signal AI Rank Report</strong> — Monthly AI-generated analysis of your biggest gains, declines, and what's driving them.</li>
        <li><strong className="text-foreground">ASIN Products Table</strong> — Drill into individual products to see per-keyword rank history, sparklines, and trends.</li>
        <li><strong className="text-foreground">Rank Drop Alerts</strong> — Set thresholds to get notified when a keyword drops significantly.</li>
        <li><strong className="text-foreground">Date range filtering</strong> — View 7-day, 30-day, 90-day, or custom date ranges.</li>
      </ul>
      <P>
        The Rank Tracker is the feedback loop. After making changes (informed by SEO Insights, validated
        by A/B Tests), come here to see if those changes actually moved the needle.
      </P>
      <ConnectsTo pages={["Dashboard", "SEO Insights", "A/B Tests", "Landscapes", "Keywords"]} />
    </PageCard>

    <SectionTitle id="analytics" scope="portfolio">Analytics</SectionTitle>
    <P>
      Analytics is a portfolio-level workspace that aggregates data across <strong>all brands</strong> in
      the workspace. The pages below ignore the Brand selector at the top of the sidebar; where filtering
      by brand makes sense, they expose their own multi-select brand filter (which is sticky on the
      Portfolio Analytics page so it persists as you scroll).
    </P>

    <PageCard icon={LayoutDashboard} title="Portfolio Analytics" route="/analytics" purpose="Cross-brand KPIs, SEO Health distribution, and rank/sales correlation across the portfolio.">
      <P>
        Top-of-page <strong>brand multi-select filter</strong> drives every card on the page — KPIs,
        SEO Health by Brand (A/B/C/D/F tranches), correlation charts, and brand-level summaries all
        respect the active selection. The filter row is sticky to keep the scope visible while scrolling.
      </P>
      <ConnectsTo pages={["Brand Comparison", "All Brand Signals", "Category Signals", "Signal ROI"]} />
    </PageCard>

    <PageCard icon={BarChart3} title="Brand Comparison" route="/analytics/brand-comparison" purpose="Side-by-side benchmark of selected brands across SEO and signal metrics.">
      <P id="analytics-brand-comparison">
        Pick two or more brands and compare them across SEO Health, signal velocity, rank movement,
        and content scorecard performance. Useful for portfolio reviews and prioritization conversations.
      </P>
    </PageCard>

    <PageCard icon={FlaskConical} title="All Brand Signals" route="/analytics/brand-signals" purpose="Every cross-brand signal that has crossed the candidate threshold, in one searchable table.">
      <P id="analytics-brand-signals">
        Aggregates signals from <strong>every brand</strong> in the workspace, excluding candidate-tier
        hypotheses (those still live on each brand's own Brand Signals page). Use this to spot patterns
        that repeat across the portfolio.
      </P>
    </PageCard>

    <PageCard icon={Layers} title="Category Signals" route="/analytics/category-signals" purpose="Recom-only view of cross-brand patterns aggregated within a category.">
      <P id="analytics-category-signals">
        Cross-brand patterns rolled up by category. Never surfaced to brand users — this is an internal
        Recom workspace for spotting category-wide trends from all tested brands.
      </P>
    </PageCard>

    <PageCard icon={BarChart3} title="Signal ROI" route="/analytics/signal-roi" purpose="Incremental sales, time savings, and optimization velocity attributable to Signal.">
      <P id="analytics-signal-roi">
        The business-case view: Incremental Sales, Annualized impact, CVR Lift, and Tests Running,
        prioritized in that order. Uses the shared A/B test stats hook with the standard 52/6
        annualization factor.
      </P>
    </PageCard>

    <SectionTitle id="spi" scope="portfolio">Content Scorecard</SectionTitle>
    <PageCard icon={ScanSearch} title="Content Scorecard" route="/spi" purpose="Standalone COSMO-aligned audit — enter any ASIN and get a full listing quality evaluation.">
      <P>
        The standalone Content Scorecard lets you audit <strong>any ASIN</strong> — it doesn't need to
        be a Hero ASIN in your brand. Enter an ASIN and Signal assembles context, sends it to the
        Lovable AI Gateway (Gemini 3 Flash) with the COSMO 15-question rubric, and returns
        per-question scores, evidence-backed findings, recommendations, dimension breakdowns, an
        overall grade, and a 3-paragraph strategic summary covering Listings, A+ Content, and Brand Store.
      </P>
      <Sub>Three-phase flow</Sub>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li><strong className="text-foreground">Entry</strong> — Input an ASIN to audit.</li>
        <li><strong className="text-foreground">Loading</strong> — Signal calls the ai-generate edge function and the LLM evaluates the listing.</li>
        <li><strong className="text-foreground">Scorecard</strong> — View per-question scores, dimension breakdown, overall grade, and strategic recommendations.</li>
      </ul>
      <P>
        For the full scoring model, COSMO question list, dimension weights, and edge-function
        implementation, see <RefLink to="ds-content-scorecard">Data Sources §7 — Content Scorecard</RefLink>.
        To audit a Hero ASIN with title and brand context already filled in, use the
        <RefLink to="content-scorecard-tab"> Content Scorecard tab inside Product Detail</RefLink>.
      </P>
      <ConnectsTo pages={["SEO Insights", "Products", "A/B Tests"]} />
    </PageCard>

    <SectionTitle id="prohibited-keywords" scope="portfolio">Prohibited Keywords</SectionTitle>
    <PageCard icon={Ban} title="Prohibited Keywords" route="/prohibited-keywords" purpose="The global blocklist — terms that no AI surface in Signal will ever generate, across every brand.">
      <P>
        Prohibited Keywords is the <strong>top of a three-tier scoping system</strong> for terms
        Signal's AI should never produce. The global list defined on this page applies to every
        brand and every product in the workspace; brand-level and product-level lists narrow the
        scope further without duplicating entries.
      </P>
      <Sub>Three scopes</Sub>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li>
          <strong className="text-foreground">Global</strong> — Managed here. Blocked across
          <em> every</em> brand and every AI feature. Best for legal-mandated terms, banned claims,
          regulated language, or anything the entire organization has agreed never to use.
        </li>
        <li>
          <strong className="text-foreground">Brand</strong> — Managed on{" "}
          <RefLink to="brand-context">Brand Context</RefLink>. Applies only when working inside
          that brand. Best for competitor names, retired SKUs, or brand-specific positioning rules.
        </li>
        <li>
          <strong className="text-foreground">Product</strong> — Managed from any Product Detail
          page via the kebab menu in the Products header. Applies only to that single ASIN. Best
          for ingredient claims, materials, or features that don't apply to one specific variant.
        </li>
      </ul>
      <Sub>Where the list is enforced</Sub>
      <P>
        Every AI surface in Signal merges the three lists before generating content. The combined
        blocklist applies to:
      </P>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li><strong className="text-foreground">Search Terms</strong> generation (the generator strips prohibited tokens from candidates before deduping).</li>
        <li><strong className="text-foreground">SEO Insights</strong> Signal AI recommendations.</li>
        <li><strong className="text-foreground">A/B Test</strong> Variant B drafting (AI-assisted build methods).</li>
        <li><strong className="text-foreground">Content Scorecard</strong> recommended fixes and strategic summaries.</li>
      </ul>
      <Sub>Managing entries</Sub>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li><strong className="text-foreground">Add Single</strong> — type a term and press Enter or click Add.</li>
        <li><strong className="text-foreground">Bulk Add</strong> — paste comma- or newline-separated terms.</li>
        <li><strong className="text-foreground">File Upload</strong> — drop a CSV or TXT file with one keyword per line (or comma-separated).</li>
        <li>All entries are normalized to lowercase and de-duplicated automatically.</li>
        <li>Remove a term by clicking the × on its pill.</li>
      </ul>
      <Sub>Quick add from search terms</Sub>
      <P>
        You don't always have to come back to this page. On <RefLink to="search-terms">Search
        Terms</RefLink>, highlighting any word in a generated set surfaces an inline{" "}
        <RefLink to="prohibited-keywords">Add to prohibited keywords</RefLink> action that lets you pick the scope (global,
        brand, or product) on the spot.
      </P>
      <ConnectsTo pages={["Brand Context", "Search Terms", "SEO Insights", "A/B Tests", "Content Scorecard"]} />
    </PageCard>
  </>
);
